/**
 * Original Life Village art, ported 1:1 from the approved design canvas.
 * Every symbol is drawn in "design units": the village is laid out on a
 * 390 × 844 portrait board, the same frame as the phone mock-ups.
 */

/** Ground fills and filters. Gradients use user space so separate layers line up. */
export const GROUND_DEFS = `
  <linearGradient id="lvGrass" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="844">
    <stop offset="0" stop-color="#7ccc52"/>
    <stop offset="1" stop-color="#5aae40"/>
  </linearGradient>
  <filter id="lvGrain" filterUnits="userSpaceOnUse" x="-600" y="-200" width="1600" height="1300">
    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.16  0 0 0 0 0.40  0 0 0 0 0.10  0 0 0 1.8 -0.75"/>
  </filter>
  <filter id="lvPatch" filterUnits="userSpaceOnUse" x="-600" y="-200" width="1600" height="1300">
    <feTurbulence type="fractalNoise" baseFrequency="0.016" numOctaves="3" seed="3"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.62  0 0 0 0 0.86  0 0 0 0 0.36  0 0 0 2.2 -0.95"/>
  </filter>
  <filter id="lvFeather" x="-0.1" y="-0.1" width="1.2" height="1.2">
    <feGaussianBlur stdDeviation="1.6"/>
  </filter>`;

const INK = `stroke="#2e2418" stroke-linejoin="round" stroke-linecap="round"`;

/** Reusable drawings. Each one is centred on its ground contact point (0,0). */
export const SYMBOLS = `
  <g id="lvTree" ${INK} stroke-width="1.3">
    <ellipse cx="3" cy="1" rx="12" ry="5" fill="#174d1b" opacity="0.35" stroke="none"/>
    <path d="M-2.5,1 L-2.5,-10 L2.5,-10 L2.5,1 Z" fill="#7a4a24"/>
    <path d="M-13,-14 C-17,-25 -8,-33 0,-32 C9,-34 16,-25 13,-15 C15,-7 6,-4 0,-6 C-7,-3 -15,-7 -13,-14 Z" fill="#3aa13c"/>
    <path d="M13,-15 C15,-7 6,-4 0,-6 C-4,-4 -9,-5 -11,-8 C-4,-7 4,-9 9,-14 C11,-17 12,-20 12,-22 C14,-20 14,-17 13,-15 Z" fill="#2a7f31" stroke="none"/>
    <path d="M-8,-22 C-8,-27 -3,-29 1,-28 C-2,-26 -5,-23 -6,-19 Z" fill="#8ce36a" stroke="none"/>
  </g>
  <g id="lvPine" ${INK} stroke-width="1.3">
    <ellipse cx="3" cy="1" rx="10" ry="4.5" fill="#174d1b" opacity="0.35" stroke="none"/>
    <path d="M-2,1 L-2,-8 L2,-8 L2,1 Z" fill="#6b3f1f"/>
    <path d="M0,-42 L9,-27 L5,-27 L13,-14 L7,-14 L15,-6 L-15,-6 L-7,-14 L-13,-14 L-5,-27 L-9,-27 Z" fill="#2e8a43"/>
    <path d="M0,-42 L9,-27 L5,-27 L13,-14 L7,-14 L15,-6 L0,-6 Z" fill="#226d35" stroke="none"/>
    <path d="M0,-38 L-4,-30 L-1.5,-30 Z M-3,-24 L-8,-16 L-5,-16 Z" fill="#7fd78a" stroke="none"/>
  </g>
  <g id="lvRock" ${INK} stroke-width="1.2">
    <path d="M-8,1 C-10,-4 -6,-8 -1,-8 C4,-9 9,-5 8,0 C6,3 -5,4 -8,1 Z" fill="#a9a196"/>
    <path d="M-5,-4 C-3,-6 0,-7 2,-6" fill="none" stroke="#d6cfc4" stroke-width="1.6"/>
  </g>
  <g id="lvTuft" stroke="#3d8a2c" stroke-width="1.3" stroke-linecap="round" fill="none">
    <path d="M-3,0 L-4.5,-5 M0,0 L0,-6.5 M3,0 L4.5,-5"/>
  </g>
  <g id="lvFlower">
    <circle cx="0" cy="0" r="1.9" fill="#ffffff"/>
    <circle cx="3.2" cy="1.2" r="1.6" fill="#ffe14a"/>
    <circle cx="1" cy="-2.6" r="1.5" fill="#ff7aa8"/>
  </g>
  <g id="lvPillar" ${INK} stroke-width="1.3">
    <path d="M-7,0 L0,4.2 L0,-9.8 L-7,-14 Z" fill="#a79f92"/>
    <path d="M0,4.2 L7,0 L7,-14 L0,-9.8 Z" fill="#8a8277"/>
    <path d="M0,-18.2 L7,-14 L0,-9.8 L-7,-14 Z" fill="#e2dacd"/>
  </g>
  <g id="lvTower" ${INK} stroke-width="1.3">
    <path d="M-8,-36 L-8,0 A8,4.5 0 0 0 8,0 L8,-36 Z" fill="#d4cab9"/>
    <path d="M2,-36 L2,4.3 A8,4.5 0 0 0 8,0 L8,-36 Z" fill="#b3a998" stroke="none"/>
    <path d="M-5,-22 L-5,-28 Q-3.5,-30.5 -2,-28 L-2,-22 Z" fill="#ffd34d"/>
    <ellipse cx="0" cy="-36" rx="9.5" ry="5" fill="#e6dfd2"/>
    <path d="M-10.5,-37 Q0,-33 10.5,-37 L0,-60 Z" fill="#3f73e6"/>
    <path d="M0,-60 L10.5,-37 Q5,-35.2 1,-35 Z" fill="#2b55bb" stroke="none"/>
    <path d="M-5,-38.5 L-1,-55" fill="none" stroke="#8fb2ff" stroke-width="1.6"/>
  </g>
  <g id="lvKeep" ${INK} stroke-width="1.3">
    <ellipse cx="6" cy="8" rx="54" ry="24" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-46,0 L0,28 L0,34 L-46,6 Z" fill="#a89e8d"/>
    <path d="M0,28 L46,0 L46,6 L0,34 Z" fill="#8c8272"/>
    <path d="M0,-28 L46,0 L0,28 L-46,0 Z" fill="#ddd4c3"/>
    <path d="M0,-20 L33,0 L0,20 L-33,0 Z" fill="none" stroke="#c3b9a7" stroke-width="1.6"/>
    <path d="M-23,-14 L23,14 M23,-14 L-23,14" fill="none" stroke="#cdc3b1" stroke-width="1.2"/>
    <use href="#lvTower" x="0" y="-18"/>
    <path d="M-30,0 L0,18 L0,-12 L-30,-30 Z" fill="#cbc1b0"/>
    <path d="M0,18 L30,0 L30,-30 L0,-12 Z" fill="#a99f8f"/>
    <path d="M0,-48 L30,-30 L0,-12 L-30,-30 Z" fill="#e4dccd"/>
    <path d="M-30,-30 L0,-12 L30,-30" fill="none" stroke="#f4eee3" stroke-width="3.5" stroke-dasharray="4 3" stroke-linecap="butt"/>
    <path d="M-22,-9 L-22,-17 Q-19.5,-21.5 -17,-14 L-17,-6 Z" fill="#ffd34d"/>
    <path d="M-13,-3.6 L-13,-11.6 Q-10.5,-16 -8,-8.6 L-8,-0.6 Z" fill="#ffd34d"/>
    <path d="M9,12.6 L9,-1.4 Q15.5,-12 22,-8.2 L22,4.8 Z" fill="#6b4226"/>
    <path d="M15.5,8.7 L15.5,-6" fill="none" stroke="#3e2614" stroke-width="1.2"/>
    <use href="#lvTower" transform="translate(0,-28) scale(1.3,1.45)"/>
    <path d="M0,-115 L0,-134" fill="none" stroke-width="1.8"/>
    <path d="M0,-134 L16,-130 L0,-125 Z" fill="#ffc928"/>
    <use href="#lvTower" x="-30" y="0"/>
    <use href="#lvTower" x="30" y="0"/>
    <use href="#lvTower" x="0" y="18"/>
  </g>
  <g id="lvWatch" ${INK} stroke-width="1.3">
    <ellipse cx="3" cy="4" rx="17" ry="7" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-11,0 L0,6.6 L0,-1.4 L-11,-8 Z" fill="#b2a998"/>
    <path d="M0,6.6 L11,0 L11,-8 L0,-1.4 Z" fill="#8f8677"/>
    <path d="M0,-14.6 L11,-8 L0,-1.4 L-11,-8 Z" fill="#d8d0c2"/>
    <path d="M-8,-7 L-6,-50 M8,-7 L6,-50 M0,-3 L0,-46 M-7.5,-14 L7.5,-38 M7.5,-14 L-7.5,-38" fill="none" stroke-width="4.4"/>
    <path d="M-8,-7 L-6,-50 M8,-7 L6,-50 M0,-3 L0,-46 M-7.5,-14 L7.5,-38 M7.5,-14 L-7.5,-38" fill="none" stroke="#c48a4a" stroke-width="2.4"/>
    <path d="M-14,-50 L0,-41.6 L0,-46.6 L-14,-55 Z" fill="#c48a4a"/>
    <path d="M0,-41.6 L14,-50 L14,-55 L0,-46.6 Z" fill="#9e6a34"/>
    <path d="M0,-63.4 L14,-55 L0,-46.6 L-14,-55 Z" fill="#dca868"/>
    <path d="M-11,-56 L-11,-71 M0,-49 L0,-62 M11,-56 L11,-71" fill="none" stroke-width="3.6"/>
    <path d="M-11,-56 L-11,-71 M0,-49 L0,-62 M11,-56 L11,-71" fill="none" stroke="#b57a3c" stroke-width="1.8"/>
    <path d="M-14,-60 L0,-51.6 L14,-60" fill="none" stroke-width="3.6"/>
    <path d="M-14,-60 L0,-51.6 L14,-60" fill="none" stroke="#dca868" stroke-width="1.8"/>
    <path d="M11,-61 L21,-66" fill="none" stroke-width="5"/>
    <path d="M11,-61 L21,-66" fill="none" stroke="#e8b23a" stroke-width="3"/>
    <path d="M-17,-70 L0,-60 L0,-92 Z" fill="#3f73e6"/>
    <path d="M0,-60 L17,-70 L0,-92 Z" fill="#2b55bb"/>
    <path d="M-11,-70 L-2,-87" fill="none" stroke="#8fb2ff" stroke-width="1.6"/>
    <path d="M0,-92 L0,-104" fill="none" stroke-width="1.6"/>
    <path d="M0,-104 L13,-100 L0,-96 Z" fill="#2fd3e8"/>
  </g>
  <g id="lvMine" ${INK} stroke-width="1.3">
    <ellipse cx="4" cy="8" rx="38" ry="12" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-34,6 C-38,-10 -26,-24 -14,-26 C-8,-38 10,-38 16,-28 C30,-26 38,-12 34,4 C24,14 -22,16 -34,6 Z" fill="#a08c72"/>
    <path d="M16,-28 C30,-26 38,-12 34,4 C26,12 10,14 4,13 C18,6 26,-8 16,-28 Z" fill="#82705a" stroke="none"/>
    <path d="M-24,-16 C-20,-24 -12,-26 -8,-31 C-5,-25 -14,-20 -20,-11 Z" fill="#c4b094" stroke="none"/>
    <path d="M-21,-6 L-16,-11 L-11,-6 L-16,-2 Z" fill="#ffd23a"/>
    <path d="M17,-17 L22,-21 L26,-14 L20,-11 Z" fill="#ffd23a"/>
    <path d="M-4,-27 L0,-31 L4,-26 L-1,-23 Z" fill="#ffd23a"/>
    <path d="M-10,11 L-10,-5 Q0,-17 10,-5 L10,11 Z" fill="#2b1d12"/>
    <path d="M-11,12 L-11,-6 M11,12 L11,-6 M-15,-8 L15,-8" fill="none" stroke-width="5.4"/>
    <path d="M-11,12 L-11,-6 M11,12 L11,-6 M-15,-8 L15,-8" fill="none" stroke="#c48a4a" stroke-width="3"/>
    <circle cx="0" cy="-12" r="2.6" fill="#ffd34d"/>
    <path d="M-5,12 L-8,24 M5,12 L8,24 M-7,16 L7,16 M-8,21 L8,21" fill="none" stroke="#6b7180" stroke-width="1.5"/>
    <path d="M9,11 L25,11 L23,21 L11,21 Z" fill="#7a808f"/>
    <path d="M9.5,11 Q13,3 17,6 Q21,3 24.5,11 Z" fill="#ffc928"/>
    <circle cx="13" cy="22" r="2.2" fill="#3a3a44"/>
    <circle cx="21" cy="22" r="2.2" fill="#3a3a44"/>
    <path d="M-33,15 Q-26,3 -18,15 Z" fill="#ffc928"/>
    <path d="M-27,8 L-25,10 M-22,7 L-22,9" fill="none" stroke="#fff6c8" stroke-width="1.4"/>
  </g>
  <g id="lvBarracks" ${INK} stroke-width="1.3">
    <ellipse cx="4" cy="6" rx="30" ry="11" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-34,17 L-34,2 M-39,7 L-29,7" fill="none" stroke-width="3.4"/>
    <path d="M-34,17 L-34,2 M-39,7 L-29,7" fill="none" stroke="#c48a4a" stroke-width="1.6"/>
    <circle cx="-34" cy="0" r="3" fill="#ead6a6"/>
    <path d="M-22,0 L0,13 L0,-3 L-22,-16 Z" fill="#cf9d62"/>
    <path d="M0,13 L22,0 L22,-16 L0,-3 Z" fill="#a87a44"/>
    <path d="M-22,-5 L0,8 M-22,-10 L0,3" fill="none" stroke="#a87a44" stroke-width="1"/>
    <path d="M-16,-6.5 L-9,-2.4 L-9,-8 L-16,-12.1 Z" fill="#ffd34d"/>
    <path d="M6,9.4 L6,-3.6 Q10.5,-9.5 15,-6.3 L15,4 Z" fill="#5a3a22"/>
    <path d="M-26,-16 L0,1 L0,-40 Z" fill="#e04a3c"/>
    <path d="M0,1 L26,-16 L0,-40 Z" fill="#b3332a"/>
    <path d="M-17,-10 L-2,-35 M-9,-5 L-1,-21" fill="none" stroke="#f07a6c" stroke-width="1.2"/>
    <circle cx="-9" cy="-14" r="5" fill="#ffc928"/>
    <path d="M-12,-17 L-6,-11 M-6,-17 L-12,-11" fill="none" stroke-width="1.6"/>
    <path d="M-24,4 L-24,-46 M24,4 L24,-46" fill="none" stroke-width="1.8"/>
    <path d="M-24,-45 L-14,-43 L-14,-30 L-19,-33.5 L-24,-30 Z" fill="#e04a3c"/>
    <path d="M24,-45 L14,-43 L14,-30 L19,-33.5 L24,-30 Z" fill="#b3332a"/>
    <circle cx="-24" cy="-47" r="1.8" fill="#ffc928"/>
    <circle cx="24" cy="-47" r="1.8" fill="#ffc928"/>
  </g>
  <g id="lvLab" ${INK} stroke-width="1.3">
    <ellipse cx="4" cy="6" rx="28" ry="10" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-27,-4 L-27,-30 L-21,-30 L-21,-4 Z" fill="#d7f7dc"/>
    <path d="M-27,-4 L-27,-17 L-21,-17 L-21,-4 Z" fill="#59e07a"/>
    <circle cx="-24" cy="-36" r="2.4" fill="#8dff9e"/>
    <circle cx="-22" cy="-42" r="1.8" fill="#8dff9e"/>
    <circle cx="-25" cy="-47" r="1.3" fill="#8dff9e"/>
    <path d="M-20,0 L0,12 L0,-2 L-20,-14 Z" fill="#8b78c9"/>
    <path d="M0,12 L20,0 L20,-14 L0,-2 Z" fill="#6a58a8"/>
    <path d="M0,-26 L20,-14 L0,-2 L-20,-14 Z" fill="#b3a3e6"/>
    <path d="M-20,-14 L0,-2 L20,-14" fill="none" stroke="#e3dbfa" stroke-width="2.6" stroke-dasharray="3 2.5" stroke-linecap="butt"/>
    <path d="M-14,-5 L-8,-1.4 L-8,-6.4 L-14,-10 Z" fill="#8dff9e"/>
    <path d="M7,7.8 L7,-2 Q10.5,-6.5 14,-4.2 L14,3.6 Z" fill="#3b2d66"/>
    <path d="M4,-24 L17,-35" fill="none" stroke-width="6.4"/>
    <path d="M4,-24 L17,-35" fill="none" stroke="#e8b23a" stroke-width="4"/>
    <path d="M-14,-14 A14,15 0 0 1 14,-14 A14,7 0 0 1 -14,-14 Z" fill="#7fe3ff"/>
    <path d="M0,-29 L0,-7 M-9,-25.5 Q-7,-14 -8,-8.6 M9,-25.5 Q7,-14 8,-8.6" fill="none" stroke-width="1"/>
    <path d="M-10,-19 Q-8,-25 -3,-26.5" fill="none" stroke="#ffffff" stroke-width="2"/>
  </g>
  <g id="lvWork" ${INK} stroke-width="1.3">
    <ellipse cx="4" cy="6" rx="30" ry="11" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <circle cx="-30" cy="10" r="3.2" fill="#e0b47a"/>
    <circle cx="-25" cy="13" r="3.2" fill="#e0b47a"/>
    <circle cx="-27.5" cy="6" r="3.2" fill="#e0b47a"/>
    <path d="M-22,0 L0,13 L0,-2 L-22,-15 Z" fill="#d6a66a"/>
    <path d="M0,13 L22,0 L22,-15 L0,-2 Z" fill="#ad7c43"/>
    <path d="M-22,-5 L0,8 M-22,-10 L0,3 M0,8 L22,-5 M0,3 L22,-10" fill="none" stroke="#9b6c38" stroke-width="1"/>
    <path d="M-16,3.5 L-16,-7 L-6,-1 L-6,9.4 Z" fill="#7a4a24"/>
    <path d="M-16,-7 L-6,9.4 M-16,3.5 L-6,-1" fill="none" stroke="#c48a4a" stroke-width="1.3"/>
    <path d="M0,-2 L22,-15 L11,-31 Z" fill="#c08a4c"/>
    <path d="M-25,-16 L1,0 L11,-31 L-15,-47 Z" fill="#7c4b2c"/>
    <path d="M-22,-24 L4,-8.5 M-19,-32 L7,-16.5 M-17,-40 L9,-24" fill="none" stroke="#5e3720" stroke-width="1.1"/>
    <path d="M11,-31 L25,-17" fill="none" stroke-width="4"/>
    <path d="M11,-31 L25,-17" fill="none" stroke="#9b5c35" stroke-width="2.2"/>
    <circle cx="11" cy="-14" r="5.2" fill="none" stroke-width="5"/>
    <circle cx="11" cy="-14" r="5.2" fill="none" stroke="#c3c9d3" stroke-width="3.4" stroke-dasharray="2.2 1.9"/>
    <circle cx="11" cy="-14" r="3.4" fill="#c3c9d3"/>
    <circle cx="11" cy="-14" r="1.2" fill="#2e2418" stroke="none"/>
    <path d="M20,9 L30,9 L28,12 L25,12 L26,16 L22,16 L23,12 L20,12 Z" fill="#6b7180"/>
  </g>
  <g id="lvTank" ${INK} stroke-width="1.3">
    <ellipse cx="3" cy="3" rx="14" ry="6" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-11,0 L0,6.6 L0,1.6 L-11,-5 Z" fill="#a79f92"/>
    <path d="M0,6.6 L11,0 L11,-5 L0,1.6 Z" fill="#8a8277"/>
    <path d="M0,-11.6 L11,-5 L0,1.6 L-11,-5 Z" fill="#d6cebf"/>
    <path d="M-7.5,-5 L-7.5,-28 A7.5,4 0 0 1 7.5,-28 L7.5,-5 A7.5,4 0 0 1 -7.5,-5 Z" fill="currentColor"/>
    <path d="M2.5,-28 L2.5,-1.2 A7.5,4 0 0 0 7.5,-5 L7.5,-28 Z" fill="#000" opacity="0.18" stroke="none"/>
    <path d="M0,-31 L4.5,-19 L0,-9 L-4.5,-19 Z" fill="#ffffff" opacity="0.6" stroke="none"/>
    <path d="M-5.5,-25 L-5.5,-9" fill="none" stroke="#ffffff" stroke-width="2.2" opacity="0.7"/>
    <path d="M-7.5,-16 A7.5,4 0 0 0 7.5,-16" fill="none" stroke="#5d6370" stroke-width="2.2"/>
    <ellipse cx="0" cy="-28" rx="8.8" ry="4.6" fill="#9aa1ad"/>
    <path d="M-6,-29 A6,5 0 0 1 6,-29 Z" fill="#c3c9d3"/>
  </g>
  <g id="lvHut" ${INK} stroke-width="1.3">
    <ellipse cx="3" cy="3" rx="16" ry="6" fill="#0e2a0c" opacity="0.22" stroke="none"/>
    <path d="M-11,0 L-11,-12 L11,-12 L11,0 A11,5 0 0 1 -11,0 Z" fill="#c99a63"/>
    <path d="M2,-12 L11,-12 L11,0 A11,5 0 0 1 2,4.9 Z" fill="#a97a45" stroke="none"/>
    <path d="M-3.5,4.6 L-3.5,-5 Q0,-9 3.5,-5 L3.5,4.6 Z" fill="#5a3a22"/>
    <path d="M-15,-10 Q0,-6 15,-10 L0,-32 Z" fill="#f2c14e"/>
    <path d="M0,-32 L15,-10 Q8,-8 0,-7.6 Z" fill="#d9a032" stroke="none"/>
    <path d="M-8,-13 L-2,-26 M6,-12 L2,-24" fill="none" stroke="#b07d22" stroke-width="1"/>
    <path d="M0,-32 L0,-37" fill="none"/>
    <path d="M7,-3 L14,-10" fill="none" stroke-width="3"/>
    <path d="M12,-12 L16,-8" fill="none" stroke="#6b7180" stroke-width="3.5"/>
  </g>
  <g id="lvGate" ${INK} stroke-width="1.3">
    <path d="M-9,4 L-9,-9 Q0,-18 9,-9 L9,4 Z" fill="#b57a3c"/>
    <path d="M-3,4 L-3,-13.5 M3,4 L3,-13.5 M-9,-3 L9,-3" fill="none" stroke="#6b4226" stroke-width="1.2"/>
    <use href="#lvPillar" x="-12" y="3"/>
    <use href="#lvPillar" x="12" y="3"/>
  </g>
  <g id="lvBubble" stroke="#2e2418" stroke-width="1.6" stroke-linejoin="round">
    <path d="M-11,-27 L11,-27 Q15,-27 15,-23 L15,-9 Q15,-5 11,-5 L4,-5 L0,1 L-4,-5 L-11,-5 Q-15,-5 -15,-9 L-15,-23 Q-15,-27 -11,-27 Z" fill="#ffffff"/>
  </g>`;
